import { useState, useEffect, useRef } from "react";
import { useBrand } from "../lib/brand";
import { usePlatformConfig } from "../lib/platformConfig";
import { useAuth } from "../lib/auth";
import { Check, ChevronDown, Plus, Star, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";

const SWATCHES = [
    "#a5a6ff",
    "#ff9ec7",
    "#8fe7d0",
    "#ffbe7b",
    "#b794f6",
    "#7dd3fc",
    "#fca5a5",
    "#fde68a",
];

export default function BrandSwitcher() {
    const {
        brands,
        currentBrand,
        currentBrandId,
        setCurrentBrandId,
        createBrand,
        renameBrand,
        deleteBrand,
        setDefaultBrand,
    } = useBrand();
    const { config } = usePlatformConfig();
    const { user: me } = useAuth();
    // Brand creation is now a super_admin-only action. Regular admins
    // can still rename/delete/set default the brands they manage, but
    // spinning up a brand new brand is reserved for the platform owner.
    const isSuper = me?.role === "super_admin";
    const primary = config?.primary_color || "#97ea3f";
    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    const [newColor, setNewColor] = useState(SWATCHES[0]);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const ref = useRef(null);
    const confirm = useConfirm();

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const onPick = (id) => {
        if (id === currentBrandId) {
            setOpen(false);
            return;
        }
        setCurrentBrandId(id);
        setOpen(false);
        toast.success(
            `Marca activa: ${brands.find((b) => b.id === id)?.name || ""}`,
        );
    };

    const onCreate = async () => {
        if (!newName.trim()) return;
        try {
            await createBrand({ name: newName.trim(), color: newColor });
            toast.success(`Marca "${newName.trim()}" creada`);
            setCreating(false);
            setNewName("");
            setNewColor(SWATCHES[0]);
            setOpen(false);
        } catch (e) {
            toast.error("Error creando marca");
        }
    };

    const onRename = async (id) => {
        if (!renameValue.trim()) return;
        await renameBrand(id, renameValue.trim());
        setRenamingId(null);
        setRenameValue("");
        toast.success("Renombrada");
    };

    const onDelete = async (id, name) => {
        const ok = await confirm({
            title: `¿Eliminar "${name}"?`,
            description:
                "La marca se desactivará. Todos sus chats, referencias y biblioteca se conservan y pueden recuperarse reactivándola.",
            confirmLabel: "Sí, eliminar",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        try {
            await deleteBrand(id);
            toast.success("Marca eliminada");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    return (
        <div ref={ref} className="relative" data-testid="brand-switcher">
            <button
                data-testid="brand-switcher-button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border hairline transition-all group bg-white hover:bg-ink-800"
                style={{
                    borderColor: `${primary}45`,
                    boxShadow: `0 1px 2px rgba(0,0,0,0.04)`,
                }}
            >
                <span
                    className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center font-display text-base"
                    style={{
                        background: primary,
                        color: config?.primary_text_color || "#000000",
                        boxShadow: `0 0 12px -2px ${primary}`,
                    }}
                    aria-hidden="true"
                >
                    {(currentBrand?.name || "?").charAt(0).toUpperCase()}
                </span>
                <span className="flex-1 text-left min-w-0 flex flex-col gap-0.5">
                    <span className="font-mono text-[9px] tracking-widest uppercase inline-flex items-center gap-1.5 text-fog-400">
                        <span
                            className="w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ background: currentBrand?.color || "#a5a6ff" }}
                            aria-hidden="true"
                        />
                        Marca activa
                    </span>
                    <span className="block text-sm text-fog-100 truncate font-semibold">
                        {currentBrand?.name || "—"}
                    </span>
                </span>
                <ChevronDown
                    className={`w-4 h-4 text-fog-400 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                    style={open ? { color: primary } : undefined}
                />
            </button>

            {open && (
                <div
                    data-testid="brand-switcher-menu"
                    className="absolute z-50 left-0 right-0 mt-2 rounded-2xl border hairline border-ink-600 bg-ink-900 shadow-glow overflow-hidden"
                >
                    <div className="max-h-72 overflow-y-auto">
                        {brands.map((b) => {
                            const active = b.id === currentBrandId;
                            const editing = renamingId === b.id;
                            return (
                                <div
                                    key={b.id}
                                    className={`group px-3 py-2.5 flex items-center gap-3 transition-colors ${
                                        active
                                            ? "bg-periwinkle-500/10"
                                            : "hover:bg-ink-800"
                                    }`}
                                >
                                    <span
                                        className="w-2.5 h-2.5 rounded-full shrink-0"
                                        style={{ background: b.color }}
                                    />
                                    {editing ? (
                                        <input
                                            autoFocus
                                            value={renameValue}
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") onRename(b.id);
                                                if (e.key === "Escape") setRenamingId(null);
                                            }}
                                            onBlur={() => setRenamingId(null)}
                                            className="flex-1 bg-ink-800 border hairline border-periwinkle-500 rounded-md px-2 py-1 text-sm text-fog-100 outline-none"
                                        />
                                    ) : (
                                        <button
                                            data-testid={`brand-pick-${b.id}`}
                                            onClick={() => onPick(b.id)}
                                            className="flex-1 text-left text-sm text-fog-100 truncate"
                                        >
                                            {b.name}
                                            {b.is_default && (
                                                <span className="ml-2 font-mono text-[9px] tracking-widest text-periwinkle-800 uppercase">
                                                    default
                                                </span>
                                            )}
                                        </button>
                                    )}
                                    {active && !editing && (
                                        <Check className="w-3.5 h-3.5 text-periwinkle-800 shrink-0" />
                                    )}
                                    {!editing && (
                                        <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                                            {/*
                                             * Brand editing/deletion is super_admin-only.
                                             * Regular admins and users can still toggle
                                             * the "default brand" star (a per-brand
                                             * preference, not a destructive action) but
                                             * may not rename or remove brands. Without
                                             * this check, any admin could wipe brands
                                             * the platform owner curated.
                                             */}
                                            {isSuper && (
                                                <ActionBtn
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRenamingId(b.id);
                                                        setRenameValue(b.name);
                                                    }}
                                                    label="rename"
                                                >
                                                    <Pencil className="w-3 h-3" />
                                                </ActionBtn>
                                            )}
                                            {!b.is_default && (
                                                <ActionBtn
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDefaultBrand(b.id);
                                                    }}
                                                    label="default"
                                                    title="Marcar como default"
                                                >
                                                    <Star className="w-3 h-3" />
                                                </ActionBtn>
                                            )}
                                            {isSuper && brands.length > 1 && (
                                                <ActionBtn
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDelete(b.id, b.name);
                                                    }}
                                                    label="delete"
                                                    danger
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </ActionBtn>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="border-t hairline border-ink-600">
                        {!isSuper ? null : creating ? (
                            <div className="p-3 space-y-2.5">
                                <input
                                    autoFocus
                                    data-testid="brand-create-name"
                                    placeholder="Nombre de la marca"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") onCreate();
                                        if (e.key === "Escape") setCreating(false);
                                    }}
                                    className="w-full bg-ink-800 border hairline border-ink-600 rounded-lg px-3 py-2 text-sm text-fog-100 outline-none focus:border-periwinkle-500"
                                />
                                <div className="flex gap-1.5 flex-wrap">
                                    {SWATCHES.map((c) => (
                                        <button
                                            key={c}
                                            type="button"
                                            onClick={() => setNewColor(c)}
                                            className={`w-6 h-6 rounded-full border-2 transition-all ${
                                                newColor === c ? "border-fog-100 scale-110" : "border-transparent"
                                            }`}
                                            style={{ background: c }}
                                            aria-label={c}
                                        />
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        data-testid="brand-create-confirm"
                                        onClick={onCreate}
                                        className="flex-1 rounded-lg bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-on text-xs font-medium py-2"
                                    >
                                        Crear
                                    </button>
                                    <button
                                        onClick={() => setCreating(false)}
                                        className="rounded-lg border hairline border-ink-600 text-fog-400 text-xs px-3 py-2 hover:text-fog-100"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                data-testid="brand-create-btn"
                                onClick={() => setCreating(true)}
                                className="w-full px-4 py-2.5 text-left text-xs font-mono uppercase tracking-widest text-periwinkle-800 hover:bg-ink-800 flex items-center gap-2"
                            >
                                <Plus className="w-3.5 h-3.5" /> Nueva marca
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function ActionBtn({ children, onClick, label, danger = false, title }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`brand-action-${label}`}
            title={title}
            className={`w-6 h-6 rounded-md border hairline border-ink-600 flex items-center justify-center transition-colors ${
                danger
                    ? "text-fog-400 hover:text-destructive hover:border-destructive"
                    : "text-fog-400 hover:text-periwinkle-800 hover:border-periwinkle-500"
            }`}
        >
            {children}
        </button>
    );
}
